import { AsyncPipe, Location, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatCard, MatCardContent } from '@angular/material/card';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { cloneDeep, find, isEqual, map as lmap } from 'lodash';
import { Observable, combineLatest, delay, merge, of, startWith, switchMap, take, tap } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { TReducerState } from '../../../app.config';
import { DynamicObservable } from '../../../core/classes/dynamic-observable';
import { PageHeaderService } from '../../../layout/page-header/page-header.service';
import { DateRangePickerComponent } from '../../../shared/components/date-range-picker/date-range-picker.component';
import { IDateRangePickerDate } from '../../../shared/components/date-range-picker/date-range-picker.interface';
import { IDropdownOption, MatDropdown } from '../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../shared/components/mat-dropdown/mat-dropdown.interface';
import { ELoadStatus } from '../../../state/state.interface';
import { userActions } from '../../../state/user/user.actions';
import {
  DateUtilitiesService,
  TDatePickerRangeOption,
  dateRangeOptionValues,
} from '../../../utilities/date-utilities.service';
import { IBoard } from '../../settings/boards/boards.interface';
import { IBoardTreeStructure, TUrlItemScope } from '../items.interface';
import {
  IItemsFilterCardFields,
  TItemScope,
  TItemType,
  TItemView,
  TItemsFilterCardFields,
  TItemsFilterCardFormControl,
} from './items-filter-card.interface';
import { IItemsFilterCardState, ItemsFilterCardStore } from './items-filter-card.store';

export interface IFilterDetails {
  data: Observable<IDropdownOption[]>;
  loading: Observable<ELoadStatus>;
}

@Component({
  imports: [
    ReactiveFormsModule,
    MatCard,
    MatCardContent,
    DateRangePickerComponent,
    AsyncPipe,
    MatButtonToggleGroup,
    MatButtonToggle,
    TranslateModule,
    MatButton,
    NgIf,
    MatDropdown,
  ],
  providers: [ItemsFilterCardStore],
  selector: 'app-items-filter-card',
  styleUrl: './items-filter-card.component.scss',
  templateUrl: './items-filter-card.component.html',
})
export class ItemsFilterCardComponent implements OnInit {
  @Input() public boardTreeStructure: IBoardTreeStructure[] = [];
  @Input() public initialBoard: IDropdownOption | undefined;
  @Input() public itemScopeValue: TUrlItemScope = null;

  @Output() public onApply = new EventEmitter<Partial<IItemsFilterCardFields>>();

  public readonly assigneeDropdownConfigurations: IDropdownSettings = {
    hasDelayedDefaultValue: true,
    hasMatError: false,
    text: this.translate.instant('filterCard.assignee'),
  };
  public readonly boardDropdownConfigurations: IDropdownSettings = {
    hasDelayedDefaultValue: true,
    hasMatError: false,
    text: this.translate.instant('filterCard.board'),
  };
  public readonly workflowStepDropdownConfigurations: IDropdownSettings = {
    hasDelayedDefaultValue: true,
    hasMatError: false,
    isClientSide: true,
    text: this.translate.instant('filterCard.workflowStep'),
  };
  public readonly itemCategoryDropdownConfigurations: IDropdownSettings = {
    hasDelayedDefaultValue: true,
    hasMatError: false,
    isClientSide: true,
    text: this.translate.instant('filterCard.itemCategory'),
  };
  public optionsList: Record<string, IFilterDetails> = {};
  public formGroup: FormGroup<TItemsFilterCardFormControl> = new FormGroup({
    assignee: new FormControl<IDropdownOption[] | null>(null),
    board: new FormControl<IDropdownOption[] | null>(null),
    dateRange: new FormControl<IDateRangePickerDate | null>(null),
    itemCategory: new FormControl<IDropdownOption[] | null>(null),
    itemScope: new FormControl<TUrlItemScope | null>(null),
    itemType: new FormControl<TItemType>('all') as FormControl<TItemType>,
    itemView: new FormControl<TItemView>('list') as FormControl<TItemView>,
    workflowStep: new FormControl<IDropdownOption[] | null>(null),
  });
  private readonly areAllFilterDropdownOptionsReady = new DynamicObservable(false);
  public showApply: Observable<boolean> = merge(
    this.areAllFilterDropdownOptionsReady.obs$.pipe(
      filter(Boolean),
      delay(200),
      switchMap(() =>
        this.onApply.pipe(
          map(() => cloneDeep(this.formGroup.value)),
          startWith(cloneDeep(this.formGroup.value)),
        ),
      ),
      switchMap((valueSinceLastApply) =>
        this.formGroup.valueChanges.pipe(map((newValue) => !isEqual(valueSinceLastApply, newValue))),
      ),
    ),
    this.onApply.pipe(map(() => false)),
  ).pipe(startWith(false));
  public itemType: TItemType = 'all';
  public itemScope: TItemScope = 'family';
  public itemView: TItemView = 'list';

  private readonly filterNames: (keyof TItemsFilterCardFormControl)[] = [
    'assignee',
    'board',
    'workflowStep',
    'itemCategory',
    'itemType',
    'itemView',
    'itemScope',
    'dateRange',
  ];

  private readonly user = this.globalStore.selectSignal((state) => state.user.user);

  constructor(
    private readonly store: ItemsFilterCardStore,
    private readonly location: Location,
    private readonly translate: TranslateService,
    private readonly activatedRoute: ActivatedRoute,
    public readonly globalStore: Store<TReducerState>,
  ) {}

  public ngOnInit(): void {
    this.prepareOptionsList();
    this.filterNames.forEach((filterName: keyof TItemsFilterCardFormControl): void => {
      // @ts-ignore
      this.formGroup.controls[filterName].reset(this.getDefaultValue(filterName));
    });

    this.activatedRoute.queryParams
      .pipe(
        take(1),
        switchMap((queryParams) => {
          const userFromParams: string | undefined = queryParams['assignee'];
          let userId = null;

          if (Number.isInteger(Number(userFromParams))) {
            userId = Number(userFromParams);
          } else if (userFromParams === 'current_user') {
            userId = this.store.user.id;
          }

          return userId
            ? this.store.assignees$.pipe(
                filter((assignees) => Boolean(assignees.length)),
                take(1),
                tap((assignees) => {
                  const selectedAssigneeOption: IDropdownOption[] = assignees.filter(
                    (assignee) => assignee.id === userId,
                  );
                  this.formGroup.controls.assignee.setValue(selectedAssigneeOption);
                }),
              )
            : new Observable<boolean>().pipe(startWith(true));
        }),
      )
      .subscribe(() => {
        this.apply();
      });
  }

  public apply(setAsDefault = false): void {
    this.updateUrlIfASingleBoardIsSelected();
    this.onApply.emit(this.formGroup.value);

    if (setAsDefault) {
      this.setAsDefault();
    }
  }

  public searchAssignees(search?: string): void {
    this.store.getAssigneeData({
      fields: ['name'],
      limit: 500 + (search ? 0 : (this.formGroup.controls.assignee.value?.length ?? 0)),
      ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
    });
  }

  public searchBoards(search?: string): void {
    this.store.getBoardData({
      fields: ['name', 'key'],
      limit: 500 + (search ? 0 : (this.formGroup.controls.board.value?.length ?? 0)),
      ...(search ? { search: { searchedFields: ['name', 'key'], searchText: search } } : {}),
    });
  }

  private prepareOptionsList(): void {
    this.filterNames.forEach((filterName: string): void => {
      this.optionsList[filterName] = this.getFilterDetails(filterName);
    });

    this.areAllFilterDropdownOptionsReady.switch(
      combineLatest(Object.values(this.optionsList).map(({ loading }) => loading)).pipe(
        map((areOptionsReady) => areOptionsReady.every((status) => status === ELoadStatus.success)),
      ),
    );
  }

  private getDefaultValue(
    filterName: keyof TItemsFilterCardFormControl,
  ): string | IDropdownOption[] | null | IDateRangePickerDate {
    const setAsDefaultValues = (this.user()?.pageConfiguration?.ItemsComponent?.find(
      ({ name }) => name === 'filterCard',
    )?.value ?? []) as TItemsFilterCardFields[];
    const setAsDefaultValue = setAsDefaultValues.find(({ name }) => name === filterName)?.value;

    switch (filterName) {
      case 'itemType':
        return (setAsDefaultValue as TItemType | undefined) ?? 'all';
      case 'itemView':
        return (setAsDefaultValue as TItemView | undefined) ?? 'list';
      case 'itemScope':
        const itemScope = this.itemScopeValue === 'self' ? 'self' : 'family';

        return this.itemScopeValue ? itemScope : ((setAsDefaultValue as TItemScope | undefined) ?? itemScope);
      case 'board':
        return this.itemScopeValue && this.initialBoard
          ? [this.initialBoard]
          : ((setAsDefaultValue as number[] | undefined)?.map((id) => ({ id, name: '...' })) ?? null);
      case 'assignee':
      case 'workflowStep':
      case 'itemCategory':
        return setAsDefaultValue
          ? ((setAsDefaultValue as number[] | undefined)?.map((id) => ({ id, name: '...' })) ?? null)
          : null;
      case 'dateRange':
        const dates = setAsDefaultValue
          ? dateRangeOptionValues[setAsDefaultValue as TDatePickerRangeOption]()
          : [null, null];

        return setAsDefaultValue
          ? { endDate: dates[1], startDate: dates[0] }
          : {
              endDate: dayjs.tz().endOf('y'),
              startDate: dayjs.tz().startOf('y'),
            };
    }
  }

  private getFilterDetails(filterName: string): IFilterDetails {
    switch (filterName) {
      case 'itemCategory':
        this.store.getItemCategoryData();

        return {
          data: this.store.itemCategories,
          loading: this.store.select((state) => state.itemCategoriesLoading),
        };
      case 'workflowStep':
        this.store.getWorkflowStepData();

        return {
          data: this.store.workflowSteps,
          loading: this.store.select((state) => state.workflowStepsLoading),
        };
      case 'board':
        this.searchBoards();

        return {
          data: this.store.boards,
          loading: this.store.select((state) => state.boardsLoading),
        };
      case 'assignee':
        this.searchAssignees();

        return {
          data: this.store.assignees$,
          loading: this.store.select((state) => state.assigneesLoading),
        };
      default:
        return {
          data: of([]),
          loading: of(ELoadStatus.success),
        };
    }
  }

  private updateUrlIfASingleBoardIsSelected(): void {
    const currentUrl: string = this.location.path();
    const scope: TUrlItemScope = this.location.path().split('/')[3] as TUrlItemScope;
    const boardKey: string | null = this.location.path().split('/')[2] as string;
    const isSelfSelected: boolean = this.formGroup.get('itemScope')?.value === 'self';
    const selectedBoards: IDropdownOption[] | null | undefined = this.formGroup.get('board')?.value;
    const selectedScope: TUrlItemScope = this.formGroup.get('itemScope')?.value === 'self' ? 'self' : 'all';
    let newUrl: string = currentUrl;
    let selectedBoard: IBoard | undefined;

    if (!selectedBoards?.length || selectedBoards?.length !== 1) {
      const [, urlParams] = currentUrl.split('?');
      const urlParamsExt = urlParams ? `?${urlParams}` : '';
      this.location.go(`/items${urlParamsExt}`);
      PageHeaderService.setPageHeader({
        icon: 'format_list_bulleted',
        showFilterButton: true,
        showFilterCard: true,
        subtitle: '',
        title: this.translate.instant('page.items.title'),
      });

      return;
    }

    if (scope === 'all' && isSelfSelected) {
      newUrl = currentUrl.replace(/all.*/, 'self');
    } else if (scope === 'self' && !isSelfSelected) {
      newUrl = currentUrl.replace(/self.*/, 'all');
    }

    this.store
      .select((state: IItemsFilterCardState) => state.boards)
      .pipe(take(1))
      .subscribe((boards: IBoard[]) => {
        selectedBoard = find(boards, { id: selectedBoards?.[0].id });

        if (selectedBoard && selectedBoards?.length) {
          newUrl = boardKey
            ? newUrl.replace(new RegExp(`${boardKey}`), selectedBoard.key)
            : newUrl.replace(/items.*/, `items/${selectedBoard.key}/${selectedScope}`);

          PageHeaderService.setPageHeader({
            icon: 'grid_view',
            subtitle: selectedBoard.key,
            title: selectedBoard.name,
          });
        }
      });

    this.location.go(newUrl);
  }

  private setAsDefault(): void {
    const componentConfigurations = cloneDeep(this.user()!.pageConfiguration?.ItemsComponent) ?? [];

    if (!componentConfigurations.some((componentConfiguration) => componentConfiguration.name === 'filterCard')) {
      componentConfigurations.push({
        name: 'filterCard',
        value: [],
      });
    }

    this.globalStore.dispatch(
      userActions.updateUserConfigurations({
        pageConfiguration: {
          ...(this.user()!.pageConfiguration ?? {}),
          ItemsComponent: componentConfigurations.map((componentConfiguration) => {
            return componentConfiguration.name === 'filterCard'
              ? {
                  name: 'filterCard',
                  value: this.formatDataForSetAsDefault(),
                }
              : componentConfiguration;
          }),
        },
      }),
    );
  }

  private formatDataForSetAsDefault(): TItemsFilterCardFields[] {
    const applyData = this.formGroup.value;
    const dateRange = DateUtilitiesService.convertRangeIntoDatePickerOption(applyData.dateRange);
    const convertedFilters = {
      ...(applyData.assignee?.length ? { assignee: lmap(applyData.assignee, 'id') } : {}),
      ...(applyData.board?.length ? { board: lmap(applyData.board, 'id') } : {}),
      ...(dateRange ? { dateRange } : {}),
      ...(applyData.itemCategory?.length ? { itemCategory: lmap(applyData.itemCategory, 'id') } : {}),
      ...(applyData.itemScope ? { itemScope: applyData.itemScope } : {}),
      ...(applyData.itemType ? { itemType: applyData.itemType } : {}),
      ...(applyData.itemView ? { itemView: applyData.itemView } : {}),
      ...(applyData.workflowStep?.length ? { workflowStep: lmap(applyData.workflowStep, 'id') } : {}),
    };

    return Object.entries(convertedFilters).map(([name, value]) => ({ name, value }) as TItemsFilterCardFields);
  }
}
